<script setup>
// Gestor de carpetas de Grupos (Victor 2026-06-05 · P2).
// Dos secciones: (1) crear/renombrar/borrar carpetas, (2) asignar cada grupo a
// una carpeta con un selector. Toda la persistencia la hace el padre (ChatList)
// en uiSettings; aquí solo emitimos los cambios. Solo Tailwind.
import { ref, computed } from 'vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  groups: { type: Array, default: () => [] }, // [{ identifier, name }]
  folders: { type: Array, default: () => [] }, // [{ id, name }]
  folderMap: { type: Object, default: () => ({}) }, // { identifier: folderId }
});

const emit = defineEmits([
  'update:show',
  'create-folder',
  'rename-folder',
  'delete-folder',
  'assign',
]);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const newFolderName = ref('');

function createFolder() {
  const name = newFolderName.value.trim();
  if (!name) return;
  emit('create-folder', name);
  newFolderName.value = '';
}

function renameFolder(folder, event) {
  const name = event.target.value.trim();
  if (!name || name === folder.name) return;
  emit('rename-folder', { id: folder.id, name });
}

function deleteFolder(id) {
  emit('delete-folder', id);
}

function assign(identifier, event) {
  emit('assign', { identifier, folderId: event.target.value || null });
}

function onClose() {
  emit('update:show', false);
}
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Carpetas de grupos"
        header-content="Crea carpetas y asigna cada grupo. Solo cambia cómo ves la lista; no toca los grupos de WhatsApp."
      />

      <div class="px-8 pt-4 pb-8 w-full">
        <!-- Sección 1 · Mis carpetas -->
        <p class="text-[11px] font-semibold tracking-wide text-n-slate-10 uppercase mb-2">
          Mis carpetas
        </p>
        <div class="flex flex-col gap-2 mb-3">
          <div
            v-for="folder in folders"
            :key="folder.id"
            class="flex gap-2 items-center"
          >
            <span class="w-2.5 h-2.5 rounded-sm bg-[#c9a35a] shrink-0" />
            <input
              :value="folder.name"
              type="text"
              class="flex-1 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-1 border border-n-weak text-n-slate-12"
              @change="renameFolder(folder, $event)"
            />
            <button
              type="button"
              class="px-2 py-1 text-xs font-medium rounded-md text-n-ruby-9 hover:bg-n-ruby-9/10"
              @click="deleteFolder(folder.id)"
            >
              Borrar
            </button>
          </div>
          <p v-if="!folders.length" class="text-xs text-n-slate-10 py-2">
            Aún no tienes carpetas. Crea la primera abajo.
          </p>
        </div>

        <div class="flex gap-2 items-center mb-6">
          <input
            v-model="newFolderName"
            type="text"
            placeholder="Nueva carpeta (ej. Contabilidad)"
            class="flex-1 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-1 border border-dashed border-[#c9a35a]/40 text-n-slate-12"
            @keyup.enter="createFolder"
          />
          <button
            type="button"
            class="px-4 py-1.5 text-xs font-semibold rounded-lg text-[#241c08] bg-[#c9a35a] hover:opacity-90"
            @click="createFolder"
          >
            Crear
          </button>
        </div>

        <!-- Sección 2 · Asignar grupos -->
        <p class="text-[11px] font-semibold tracking-wide text-n-slate-10 uppercase mb-2">
          Asignar grupos ({{ groups.length }})
        </p>
        <div class="flex flex-col gap-1 max-h-[280px] overflow-y-auto">
          <div
            v-for="group in groups"
            :key="group.identifier"
            class="flex gap-3 items-center py-1.5 border-b border-n-weak/60"
          >
            <span class="flex-1 text-sm truncate text-n-slate-12">{{ group.name }}</span>
            <select
              :value="folderMap[group.identifier] || ''"
              class="px-2 py-1 text-xs rounded-md bg-n-alpha-1 border border-n-weak text-n-slate-11 max-w-[150px]"
              @change="assign(group.identifier, $event)"
            >
              <option value="">Sin carpeta</option>
              <option v-for="folder in folders" :key="folder.id" :value="folder.id">
                {{ folder.name }}
              </option>
            </select>
          </div>
          <p v-if="!groups.length" class="text-xs text-n-slate-10 py-2">
            No hay grupos para asignar.
          </p>
        </div>

        <div class="flex justify-end mt-6">
          <button
            type="button"
            class="px-5 py-2 text-sm font-semibold rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
            @click="onClose"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
